import { v4 as uuidv4 } from 'uuid';
import createClient from 'openapi-fetch';
import type { paths } from './schemas/text-schema';

// Base URL construction using region parameter
const getBaseUrl = (region: string): string => {
  return `https://llm-studio.skit.ai/${region}/api/text`;
};


export interface ApiConfig {
  clientId: string;
  assistantId: string;
  region: string;
}

export interface PostMessageParams {
  message: string;
  clientId: string;
  assistantId: string;
  userId?: string;
  region: string;
  resetConversation?: boolean;
  recipientId?: string;
}

// Using types from generated schema
export type WebSdkConfigResponse = paths['/text_runtime/websdk/config']['get']['responses'][200]['content']['application/json'];
export type PostMessageResponse = paths['/text_runtime/post_message_web']['post']['responses'][200]['content']['application/json'];
export type PostMessageRequestBody = paths['/text_runtime/post_message_web']['post']['requestBody']['content']['application/json'];

// Ensure output_message is always a string
export interface SafePostMessageResponse extends Omit<PostMessageResponse, 'output_message'> {
  output_message: string;
}

// Error type for API responses
export type ApiErrorResponse = {
  detail: string;
};

// Session storage for recipient IDs
const sessionRecipientIds: Record<string, string> = {};

// Helper to get or create recipient ID for a session
const getOrCreateRecipientId = (clientId: string, assistantId: string, providedId?: string): string => {
  const sessionKey = `${clientId}:${assistantId}`;
  
  // If a recipient ID is provided, store it and return it
  if (providedId) {
    sessionRecipientIds[sessionKey] = providedId;
    return providedId;
  }
  
  // If we already have a recipient ID for this session, return it
  if (sessionRecipientIds[sessionKey]) {
    return sessionRecipientIds[sessionKey];
  }
  
  // Otherwise, create a new one, store it, and return it
  const newRecipientId = `anonymous-user-${uuidv4()}`;
  sessionRecipientIds[sessionKey] = newRecipientId;
  return newRecipientId;
};

// Create API client factory function
const createApiClientInstance = (baseUrl: string) => {
  const client = createClient<paths>({
    baseUrl,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Origin': window.location.origin,
    },
  });

  return client;
};

/**
 * Fetches the WebSDK configuration from the server
 */
export const fetchConfig = async (
  clientId: string,
  assistantId: string,
  region: string
): Promise<WebSdkConfigResponse> => {
  try {
    const baseUrl = getBaseUrl(region);
    const client = createApiClientInstance(baseUrl);

    const { data, error } = await client.GET('/text_runtime/websdk/config', {
      params: {
        query: {
          client_uuid: clientId,
          assistant_uuid: assistantId,
        }
      }
    });

    if (error) {
      console.error('API error:', error);
      throw new Error('Failed to fetch WebSDK config');
    }

    return data || {
      client_uuid: clientId,
      assistant_uuid: assistantId,
      primary_color: null,
      secondary_color: null,
      chatbot_name: null,
      header_content: null,
      error: null
    };
  } catch (error) {
    console.error('Failed to fetch WebSDK config:', error);
    return {
      client_uuid: clientId,
      assistant_uuid: assistantId,
      primary_color: null,
      secondary_color: null,
      chatbot_name: null,
      header_content: null,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Sends a message to the chat API
 */
export const sendMessage = async (params: PostMessageParams): Promise<SafePostMessageResponse> => {
  const { message, clientId, assistantId, region, resetConversation = false, recipientId } = params;

  // Get or create a recipient ID for this session
  const sessionRecipientId = getOrCreateRecipientId(clientId, assistantId, recipientId);

  try {
    const baseUrl = getBaseUrl(region);
    const client = createApiClientInstance(baseUrl);

    const requestBody = {
      channel: 'WEB_CHAT',
      virtual_id: 'web-chat',
      recipient_id: sessionRecipientId,
      user_message: message,
      reset_interaction: resetConversation,
      assistant_uuid: assistantId,
      interaction_metadata: {},
      is_test: false,
    } satisfies PostMessageRequestBody;

    const { data, error } = await client.POST('/text_runtime/post_message_web', {
      body: requestBody
    });

    if (error) {
      console.error('API error:', error);
      throw new Error('Failed to send message');
    }

    // Ensure output_message is a string
    const safeData: SafePostMessageResponse = {
      ...data!,
      output_message: data?.output_message || ''
    };

    return safeData;
  } catch (error) {
    console.error('Failed to send message:', error);
    throw error;
  }
};

// For backwards compatibility - create a textApiClient that implements the GET method
export const textApiClient = {
  GET: async (endpoint: string, options: { params?: { query?: Record<string, string> } } = {}) => {
    try {
      const { params } = options;
      const region = params?.query?.region || 'us';
      
      const baseUrl = getBaseUrl(region);
      
      // For websdk/config endpoint
      if (endpoint === '/text_runtime/websdk/config' && params?.query) {
        const clientId = params.query.client_uuid;
        const assistantId = params.query.assistant_uuid;
        
        if (clientId && assistantId) {
          const data = await fetchConfig(clientId, assistantId, region);
          return { data };
        }
      }
      
      // For other endpoints, use direct fetch since the types may not match exactly
      const queryString = params?.query 
        ? '?' + new URLSearchParams(params.query).toString() 
        : '';
      
      const url = `${baseUrl}${endpoint}${queryString}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Origin': window.location.origin,
        }
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      return { data };
    } catch (error) {
      console.error(`Failed to execute GET ${endpoint}:`, error);
      throw error;
    }
  }
};



